import React, { useState, useEffect, useRef } from "react";
import { useParams, useLoaderData, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Panel } from "primereact/panel";
import { Card } from "primereact/card";
import { Avatar } from "primereact/avatar";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";
import useUrlState from "@ahooksjs/use-url-state";
import MeaPage from "../../../components/MeaPage";
import SecretariatWorkspaceBar from "../components/SecretariatWorkspaceBar";
import MeaDataTable from "../../../components/MeaDataTable";
import studentsAPI from "../../../services/students";
import classesAPI from "../../../services/classes";
import slotsAPI from "../../../services/slots";
import teachersAPI from "../../../services/teachers";
import classroomsAPI from "../../../services/classrooms";
import FamilyInfoModal from "../../registrations/components/FamilyInfoModal";

// StudentCard component for mobile layout
const StudentCard = ({ student, schoolId, trainingId, onFamilyClick }) => {
  const firstAffectation = student.affectations?.[0];
  
  const getTeacherName = () => {
    const teacher = firstAffectation?.teacher;
    if (teacher?.member?.name) {
      return teacher.member.name;
    } else if (teacher?.member?.lastname && teacher?.member?.firstname) {
      return `${teacher.member.lastname} ${teacher.member.firstname}`;
    }
    return "-";
  };

  return (
    <Card 
      className="cursor-pointer hover:shadow-lg transition-shadow duration-200 w-full"
      onClick={() => onFamilyClick(student)}
    >
      <div className="p-2">
        {/* Header with student name and family button */}
        <div className="flex justify-content-between align-items-start mb-3">
          <div className="flex align-items-center gap-3">
            <Avatar 
              icon="pi pi-user" 
              size="large" 
              className="bg-primary text-white"
            />
            <div>
              <h3 className="text-lg font-semibold text-gray-800 m-0">
                {student.lastname} {student.firstname}
              </h3>
              <p className="text-sm text-gray-600 m-0">
                {student.member_id || "Immatriculation non disponible"}
              </p>
            </div>
          </div>
          <Button
            icon="pi pi-users"
            size="small"
            outlined
            tooltip="Voir les informations familiales"
            onClick={(e) => {
              e.stopPropagation();
              onFamilyClick(student);
            }}
          />
        </div>

        {/* Student details */}
        <div className="grid">
          <div className="col-12">
            <div className="flex flex-column gap-2">
              <div className="flex justify-content-between">
                <span className="text-600 text-sm font-medium">Classe:</span>
                <span className="text-900 text-sm">
                  {student?.classe?.name || "-"}
                </span>
              </div>
              <div className="flex justify-content-between">
                <span className="text-600 text-sm font-medium">Niveau:</span>
                <span className="text-900 text-sm">
                  {student?.level?.name || "-"}
                </span>
              </div>
              <div className="flex justify-content-between">
                <span className="text-600 text-sm font-medium">Créneau:</span>
                <span className="text-900 text-sm">
                  {firstAffectation?.slot?.name || "-"}
                </span>
              </div>
              <div className="flex justify-content-between">
                <span className="text-600 text-sm font-medium">Heures:</span>
                <span className="text-900 text-sm">
                  {firstAffectation?.start_hour && firstAffectation?.end_hour 
                    ? `${firstAffectation.start_hour} - ${firstAffectation.end_hour}`
                    : "-"
                  }
                </span>
              </div>
              <div className="flex justify-content-between">
                <span className="text-600 text-sm font-medium">Enseignant:</span>
                <span className="text-900 text-sm">
                  {getTeacherName()}
                </span>
              </div>
              <div className="flex justify-content-between">
                <span className="text-600 text-sm font-medium">Salle:</span>
                <span className="text-900 text-sm">
                  {firstAffectation?.classroom?.name || "-"}
                </span>
              </div>
              <div className="flex justify-content-between align-items-center">
                <span className="text-600 text-sm font-medium">Absences:</span>
                {(student?.absences_count ?? 0) > 0 ? (
                  <Tag severity="danger" value={student.absences_count} />
                ) : (
                  <span className="text-900 text-sm">0</span>
                )}
              </div>
              <div className="flex justify-content-between align-items-center">
                <span className="text-600 text-sm font-medium">Retards:</span>
                {(student?.retards_count ?? 0) > 0 ? (
                  <Tag severity="warning" value={student.retards_count} />
                ) : (
                  <span className="text-900 text-sm">0</span>
                )}
              </div>
              {firstAffectation?.classroom?.location && (
                <div className="flex justify-content-between">
                  <span className="text-600 text-sm font-medium">Localisation:</span>
                  <span className="text-900 text-sm">
                    {firstAffectation.classroom.location}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

// Loading skeleton for cards
const StudentCardSkeleton = () => (
  <Card className="w-full">
    <div className="p-2">
      <div className="flex align-items-center gap-3 mb-3">
        <Skeleton shape="circle" size="4rem" />
        <div className="flex-1">
          <Skeleton width="80%" height="1.5rem" className="mb-2" />
          <Skeleton width="60%" height="1rem" />
        </div>
        <Skeleton width="2.5rem" height="2.5rem" />
      </div>
      <div className="space-y-2">
        {[...Array(6)].map((_, index) => (
          <div key={index} className="flex justify-content-between">
            <Skeleton width="30%" height="1rem" />
            <Skeleton width="40%" height="1rem" />
          </div>
        ))}
      </div>
    </div>
  </Card>
);

const SecretariatStudentsDetailPage = () => {
  const { training } = useLoaderData();
  const { schoolId, trainingId } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuthContext();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [searchName, setSearchName] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  

  const familyInfoModalRef = useRef();

  // Mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const accessToken = auth?.access_token;

  // URL state management for filters
  const [state, setState] = useUrlState({
    studentsPage: "1",
    studentsFilter: JSON.stringify({
      name: "",
      class_id: "",
      slot_id: "",
      teacher_id: "",
      classroom_id: "",
      min_absences: "",
      min_retards: "",
    }),
  });

  // Parse filter state
  const filterState = JSON.parse(state.studentsFilter);

  // Check if any filters are active
  const hasActiveFilters = filterState.class_id || filterState.slot_id || filterState.teacher_id || filterState.classroom_id || filterState.min_absences || filterState.min_retards;

  // Get active filter labels for display
  const getActiveFilterLabels = () => {
    const labels = [];

    if (filterState.class_id) {
      const selectedClass = classesQuery?.data?.data?.find(cls => cls.id === filterState.class_id);
      if (selectedClass) labels.push(`Classe: ${selectedClass.name}`);
    }

    if (filterState.slot_id) {
      const selectedSlot = slotsQuery?.data?.data?.find(slot => slot.id === filterState.slot_id);
      if (selectedSlot) labels.push(`Créneau: ${selectedSlot.name}`);
    }

    if (filterState.teacher_id) {
      const selectedTeacher = teachersQuery?.data?.data?.find(teacher => teacher.id === filterState.teacher_id);
      if (selectedTeacher) {
        const teacherName = selectedTeacher.member?.name || `${selectedTeacher.member?.lastname} ${selectedTeacher.member?.firstname}`;
        labels.push(`Enseignant: ${teacherName}`);
      }
    }

    if (filterState.classroom_id) {
      const selectedClassroom = classroomsQuery?.data?.data?.find(classroom => classroom.id === filterState.classroom_id);
      if (selectedClassroom) labels.push(`Salle: ${selectedClassroom.name}`);
    }

    if (filterState.min_absences) {
      labels.push(`Absences ≥ ${filterState.min_absences}`);
    }

    if (filterState.min_retards) {
      labels.push(`Retards ≥ ${filterState.min_retards}`);
    }

    return labels;
  };

  // Update searchName when filter changes
  useEffect(() => {
    setSearchName(filterState.name);
  }, [filterState.name]);

  const queryList = studentsAPI.useGetEnrolledList(
    schoolId,
    trainingId,
    accessToken,
    page,
    pageSize,
    filterState
  );

  // Fetch filter options
  const classesQuery = classesAPI.useGetList(schoolId, trainingId, accessToken, 1, 1000);
  const slotsQuery = slotsAPI.useGetList(schoolId, trainingId, accessToken, 1, 1000);
  const teachersQuery = teachersAPI.useGetList(schoolId, trainingId, accessToken, 1, 1000);
  const classroomsQuery = classroomsAPI.useGetList(schoolId, trainingId, accessToken, 1, 1000);

  // Custom body template for student name with lastname ordering
  const nameBodyTemplate = (rowData) => {
    return (
      <div 
        className="flex flex-column cursor-pointer hover:bg-blue-50 p-2 border-round"
        onClick={() => familyInfoModalRef.current?.show(rowData)}
        style={{ transition: 'background-color 0.2s' }}
      >
        <span><span className="font-semibold">{rowData.lastname}</span> {rowData.firstname}</span>
      </div>
    );
  };

  // Custom body template for class information
  const classBodyTemplate = (rowData) => {
    const firstAffectation = rowData.affectations?.[0];
    return (
      <div className="flex flex-column">
        <span className="font-medium">{rowData?.classe?.name || "-"}</span>
        <span className="text-sm text-600">{rowData?.level?.name || ""}</span>
      </div>
    );
  };

  // Custom body template for slot information
  const slotBodyTemplate = (rowData) => {
    const firstAffectation = rowData.affectations?.[0];
    return (
      <div className="flex flex-column">
        <span className="font-medium">{firstAffectation?.slot?.name || "-"}</span>
        <span className="text-sm text-600">
          {firstAffectation?.start_hour && firstAffectation?.end_hour 
            ? `${firstAffectation.start_hour} - ${firstAffectation.end_hour}`
            : ""
          }
        </span>
      </div>
    );
  };

  // Custom body template for teacher information
  const teacherBodyTemplate = (rowData) => {
    const firstAffectation = rowData.affectations?.[0];
    const teacher = firstAffectation?.teacher;
    
    let teacherName = "-";
    if (teacher?.member?.name) {
      teacherName = teacher.member.name;
    } else if (teacher?.member?.lastname && teacher?.member?.firstname) {
      teacherName = `${teacher.member.lastname} ${teacher.member.firstname}`;
    }
    
    return (
      <div className="flex flex-column">
        <span className="font-medium">{teacherName}</span>
        <span className="text-sm text-600">{teacher?.member?.email || ""}</span>
      </div>
    );
  };

  // Custom body template for classroom information
  const classroomBodyTemplate = (rowData) => {
    const firstAffectation = rowData.affectations?.[0];
    return (
      <div className="flex flex-column">
        <span className="font-medium">{firstAffectation?.classroom?.name || "-"}</span>
        <span className="text-sm text-600">{firstAffectation?.classroom?.location || ""}</span>
      </div>
    );
  };


  // Filter function
  const setFilter = (field, value) => {
    let currentFilter = state?.studentsFilter;
    let currentFilterObj = JSON.parse(currentFilter);
    currentFilterObj[field] = value;
    setState((s) => ({
      studentsPage: "1",
      studentsFilter: JSON.stringify(currentFilterObj),
    }));
    setPage(1);
  };

  const handleSearch = () => {
    setFilter("name", searchName);
  };

  const handleClearSearch = () => {
    setSearchName("");
    setFilter("name", "");
  };

  const handleClearAllFilters = () => {
    setSearchName("");
    setState({
      studentsPage: "1",
      studentsFilter: JSON.stringify({
        name: "",
        class_id: "",
        slot_id: "",
        teacher_id: "",
        classroom_id: "",
        min_absences: "",
        min_retards: "",
      }),
    });
    setPage(1);
  };

  const handlePrintAllStudents = () => {
    // Build query parameters from current filters
    const queryParams = new URLSearchParams();

    if (filterState.name) queryParams.append('name', filterState.name);
    if (filterState.class_id) queryParams.append('class_id', filterState.class_id);
    if (filterState.slot_id) queryParams.append('slot_id', filterState.slot_id);
    if (filterState.teacher_id) queryParams.append('teacher_id', filterState.teacher_id);
    if (filterState.classroom_id) queryParams.append('classroom_id', filterState.classroom_id);
    if (filterState.min_absences) queryParams.append('min_absences', filterState.min_absences);
    if (filterState.min_retards) queryParams.append('min_retards', filterState.min_retards);
    
    const queryString = queryParams.toString();
    const url = queryString 
      ? `/school/${schoolId}/secretariat/students/${trainingId}/print-all-students?${queryString}`
      : `/school/${schoolId}/secretariat/students/${trainingId}/print-all-students`;
    
    navigate(url);
  };

  const customActions = (
    <div className="flex gap-2">
      <Button
        label="Imprimer"
        icon="pi pi-print"
        className="mr-2"
        onClick={handlePrintAllStudents}
      />
    </div>
  );

  return (
    <MeaPage
      title={`Liste des élèves - ${training?.name || "Formation"}`}
      breadcrumb={<SecretariatWorkspaceBar section="students" training={training} />}
      showBackButton={true}
      onBackButtonClick={() => navigate(`/school/${schoolId}/secretariat`)}
      customActions={customActions}
    >
      {/* Filter Panel */}
      <div className="mb-4">
        <Panel 
          header={
            <div className="flex flex-column gap-1">
              <span>Filtres</span>
              {hasActiveFilters && (
                <div className="flex flex-wrap gap-1">
                  {getActiveFilterLabels().map((label, index) => (
                    <span key={index} className="text-xs text-600 bg-gray-100 px-2 py-1 border-round">
                      {label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          } 
          toggleable 
          collapsed={!hasActiveFilters}
        >
          <div className="grid">
            <div className="col-12 md:col-6 lg:col-3">
              <label className="block text-sm font-medium mb-2">Classe</label>
              <Dropdown
                value={filterState.class_id}
                options={[
                  { label: "Toutes les classes", value: "" },
                  ...(classesQuery?.data?.data || []).map(cls => ({
                    label: cls.name,
                    value: cls.id
                  }))
                ]}
                onChange={(e) => setFilter("class_id", e.value)}
                placeholder="Sélectionner une classe"
                className="w-full"
                showClear
              />
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <label className="block text-sm font-medium mb-2">Créneau</label>
              <Dropdown
                value={filterState.slot_id}
                options={[
                  { label: "Tous les créneaux", value: "" },
                  ...(slotsQuery?.data?.data || []).map(slot => ({
                    label: `${slot.name}`,
                    value: slot.id
                  }))
                ]}
                onChange={(e) => setFilter("slot_id", e.value)}
                placeholder="Sélectionner un créneau"
                className="w-full"
                showClear
              />
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <label className="block text-sm font-medium mb-2">Enseignant</label>
              <Dropdown
                value={filterState.teacher_id}
                options={[
                  { label: "Tous les enseignants", value: "" },
                  ...(teachersQuery?.data?.data || []).map(teacher => ({
                    label: teacher.member?.name || `${teacher.member?.lastname} ${teacher.member?.firstname}`,
                    value: teacher.id
                  }))
                ]}
                onChange={(e) => setFilter("teacher_id", e.value)}
                placeholder="Sélectionner un enseignant"
                className="w-full"
                showClear
              />
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <label className="block text-sm font-medium mb-2">Salle</label>
              <Dropdown
                value={filterState.classroom_id}
                options={[
                  { label: "Toutes les salles", value: "" },
                  ...(classroomsQuery?.data?.data || []).map(classroom => ({
                    label: classroom.name,
                    value: classroom.id
                  }))
                ]}
                onChange={(e) => setFilter("classroom_id", e.value)}
                placeholder="Sélectionner une salle"
                className="w-full"
                showClear
              />
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <label className="block text-sm font-medium mb-2">Absences (min.)</label>
              <InputNumber
                value={filterState.min_absences === "" ? null : Number(filterState.min_absences)}
                onValueChange={(e) => setFilter("min_absences", e.value ?? "")}
                min={0}
                placeholder="Nombre d'absences"
                className="w-full"
                showButtons={false}
              />
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <label className="block text-sm font-medium mb-2">Retards (min.)</label>
              <InputNumber
                value={filterState.min_retards === "" ? null : Number(filterState.min_retards)}
                onValueChange={(e) => setFilter("min_retards", e.value ?? "")}
                min={0}
                placeholder="Nombre de retards"
                className="w-full"
                showButtons={false}
              />
            </div>
          </div>
          <div className="flex justify-content-end mt-3">
            <Button
              label="Effacer tous les filtres"
              icon="pi pi-times"
              size="small"
              severity="secondary"
              outlined
              onClick={handleClearAllFilters}
            />
          </div>
        </Panel>
      </div>

      {/* Search Section */}
      <div className="mb-4">
        <div className="flex flex-column md:flex-row gap-3 align-items-center">
          <div className="flex-1 w-full md:w-auto">
            <InputText
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              placeholder="Rechercher par nom ou prénom..."
              className="w-full"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div className="flex gap-2 w-full md:w-auto">
            <Button
              label="Rechercher"
              icon="pi pi-search"
              onClick={handleSearch}
              className="p-button-outlined flex-1 md:flex-none"
            />
            <Button
              label="Effacer"
              icon="pi pi-times"
              onClick={handleClearSearch}
              className="p-button-outlined flex-1 md:flex-none"
              severity="secondary"
            />
          </div>
        </div>
      </div>

      {/* Desktop Table View */}
      {!isMobile && (
        <div>
          {queryList.isLoading ? (
            <div className="border-1 border-gray-200 border-round">
              {/* Table Header Skeleton */}
              <div className="flex bg-gray-50 p-3 border-bottom-1 border-gray-200">
                {[...Array(5)].map((_, index) => (
                  <div key={index} className="flex-1 px-3">
                    <Skeleton width="80%" height="1.5rem" />
                  </div>
                ))}
              </div>
              
              {/* Table Rows Skeleton */}
              {[...Array(5)].map((_, rowIndex) => (
                <div key={rowIndex} className="flex p-3 border-bottom-1 border-gray-200">
                  {[...Array(5)].map((_, colIndex) => (
                    <div key={colIndex} className="flex-1 px-3">
                      <Skeleton width="90%" height="1.2rem" />
                    </div>
                  ))}
                </div>
              ))}
              
              {/* Pagination Skeleton */}
              <div className="flex justify-content-between align-items-center p-3">
                <Skeleton width="200px" height="1.5rem" />
                <div className="flex gap-2">
                  <Skeleton width="2rem" height="2rem" />
                  <Skeleton width="2rem" height="2rem" />
                  <Skeleton width="2rem" height="2rem" />
                  <Skeleton width="2rem" height="2rem" />
                </div>
              </div>
            </div>
          ) : queryList.isError ? (
            <div className="text-center p-8">
              <p className="text-red-500">Erreur lors du chargement des élèves</p>
              <Button 
                label="Réessayer" 
                onClick={() => queryList.refetch()} 
                className="mt-4"
              />
            </div>
          ) : (
            <MeaDataTable
              query={queryList}
              serviceAPI={studentsAPI}
              names={{
                pagination: "students",
                singular: "élève",
                plural: "élèves",
              }}
              showDeleteButton={false}
              showFilterButton={false}
              setPage={setPage}
              setPageSize={setPageSize}       
              paginatorPosition="both"
              schoolId={schoolId}
              trainingId={trainingId}
            >
              <Column
                field="lastname"
                header="Nom"
                body={nameBodyTemplate}          
                style={{ minWidth: "200px" }}
              />
              <Column
                field="affectations.0.classe.name"
                header="Classe"
                body={classBodyTemplate}          
                style={{ minWidth: "150px" }}
              />
              <Column
                field="affectations.0.slot.name"
                header="Créneau"
                body={slotBodyTemplate}          
                style={{ minWidth: "150px" }}
              />
              <Column
                field="affectations.0.teacher.lastname"
                header="Enseignant"
                body={teacherBodyTemplate}          
                style={{ minWidth: "180px" }}
              />
              <Column
                field="affectations.0.classroom.name"
                header="Salle"
                body={classroomBodyTemplate}
                style={{ minWidth: "150px" }}
              />
              <Column
                field="absences_count"
                header="Absences"
                headerStyle={{ textAlign: "center", width: "7rem" }}
                bodyStyle={{ textAlign: "center" }}
                body={(student) =>
                  (student?.absences_count ?? 0) > 0 ? (
                    <Tag severity="danger" value={student.absences_count} />
                  ) : (
                    0
                  )
                }
              />
              <Column
                field="retards_count"
                header="Retards"
                headerStyle={{ textAlign: "center", width: "7rem" }}
                bodyStyle={{ textAlign: "center" }}
                body={(student) =>
                  (student?.retards_count ?? 0) > 0 ? (
                    <Tag severity="warning" value={student.retards_count} />
                  ) : (
                    0
                  )
                }
              />
            </MeaDataTable>
          )}
        </div>
      )}

      {/* Mobile Card View */}
      {isMobile && (
        <div>
          {queryList.isLoading ? (
            <div className="grid gap-2">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="col-12">
                  <StudentCardSkeleton />
                </div>
              ))}
            </div>
          ) : queryList.isError ? (
            <div className="text-center p-8">
              <p className="text-red-500">Erreur lors du chargement des élèves</p>
              <Button 
                label="Réessayer" 
                onClick={() => queryList.refetch()} 
                className="mt-4"
              />
            </div>
          ) : (
            <div>
              {queryList.data?.data && queryList.data.data.length > 0 ? (
                <>
                  <div className="grid gap-2">
                    {queryList.data.data.map((student) => (
                      <div key={student.id} className="col-12">
                        <StudentCard
                          student={student}
                          schoolId={schoolId}
                          trainingId={trainingId}
                          onFamilyClick={(studentData) => familyInfoModalRef.current?.show(studentData)}
                        />
                      </div>
                    ))}
                  </div>
                  
                  {/* Mobile Pagination */}
                  {queryList.data.total > pageSize && (
                    <div className="flex justify-content-center mt-4">
                      <div className="flex align-items-center gap-2">
                        <Button
                          icon="pi pi-chevron-left"
                          disabled={page === 1}
                          onClick={() => setPage(page - 1)}
                          size="small"
                          outlined
                        />
                        <span className="text-sm">
                          Page {page} sur {Math.ceil(queryList.data.total / pageSize)}
                        </span>
                        <Button
                          icon="pi pi-chevron-right"
                          disabled={page >= Math.ceil(queryList.data.total / pageSize)}
                          onClick={() => setPage(page + 1)}
                          size="small"
                          outlined
                        />
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center p-8">
                  <div className="flex flex-column align-items-center gap-3">
                    <i className="pi pi-users text-6xl text-400"></i>
                    <div>
                      <h3 className="text-lg font-semibold text-600 mb-2">Aucun élève trouvé</h3>
                      <p className="text-sm text-500 mb-4">
                        {hasActiveFilters 
                          ? "Aucun élève ne correspond aux filtres sélectionnés. Essayez de modifier vos critères de recherche."
                          : "Aucun élève n'est inscrit dans cette formation pour le moment."
                        }
                      </p>
                      {hasActiveFilters && (
                        <Button
                          label="Effacer les filtres"
                          icon="pi pi-times"
                          size="small"
                          severity="secondary"
                          outlined
                          onClick={handleClearAllFilters}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <FamilyInfoModal 
        ref={familyInfoModalRef} 
        schoolId={schoolId} 
        trainingId={trainingId} 
      />
    </MeaPage>
  );
};

export default SecretariatStudentsDetailPage;
